import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { type Campaign, createIdeaBatch, type Repository } from '../api.ts';
import { describeError } from '../data.tsx';
import { ideaLines, looksLikeTable, tableRowLabels } from '../ideaText.ts';
import { Link } from '../router.tsx';
import { Notice } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * Quick add (US-029): the composer at the top of the Ideas column. One idea
 * per line, or a pasted planning table, for one primary repository and
 * optionally a campaign; it posts a batch exactly as the New ideas form
 * would, and stays open for the next one. The new ideas come in on the
 * board's next poll as "Waiting to be worked out".
 */

/** The server's ceiling on a batch of typed ideas. */
const MAX_IDEAS = 50;
/** How much of the first idea names the batch. */
export const QUICK_ADD_TITLE_LENGTH = 60;
/** The repository the last quick add was for. */
const REPOSITORY_KEY = 'mc.board.quickAdd.repository';

/** The batch title: the first idea cut to 60 characters, plus " (+{n} more)" when there are several. */
export function quickAddTitle(items: readonly string[]): string {
  const first = (items[0] ?? '').slice(0, QUICK_ADD_TITLE_LENGTH).trim();
  return items.length > 1 ? `${first} (+${String(items.length - 1)} more)` : first;
}

/** Storage can throw (a private window, a full quota); nothing is remembered then. */
function readLastRepository(): string | null {
  try {
    return window.localStorage.getItem(REPOSITORY_KEY);
  } catch {
    return null;
  }
}

function writeLastRepository(id: string): void {
  try {
    window.localStorage.setItem(REPOSITORY_KEY, id);
  } catch {
    // Not remembered this time; the next quick add starts on the first repository.
  }
}

export function QuickAdd({
  repositories,
  campaigns,
  campaignFilter,
  onAdded,
  onClose,
}: {
  /** Every repository; only the ones with a deploy key are offered. `null` while they load. */
  readonly repositories: readonly Repository[] | null;
  /** The campaigns an idea can start in (archived ones are left out by the caller). */
  readonly campaigns: readonly Campaign[];
  /** The board's campaign filter: a campaign id, `none` or `all`. */
  readonly campaignFilter: string;
  readonly onAdded: (count: number) => void;
  readonly onClose: () => void;
}) {
  const id = useId();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const usable = (repositories ?? []).filter((repository) => repository.keyConfigured);
  const remembered = readLastRepository();
  const fallbackRepository =
    usable.find((repository) => repository.id === remembered)?.id ?? usable[0]?.id ?? '';
  const [repositoryId, setRepositoryId] = useState<string | null>(null);
  const repository = usable.some((one) => one.id === repositoryId) ? (repositoryId ?? '') : fallbackRepository;
  const [campaignId, setCampaignId] = useState(() =>
    campaigns.some((campaign) => campaign.id === campaignFilter) ? campaignFilter : '',
  );
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On opening, and back after each add (the box is disabled while it posts).
  useEffect(() => {
    if (!busy) textRef.current?.focus();
  }, [busy]);

  const isTable = looksLikeTable(text);
  const items = isTable ? tableRowLabels(text) : ideaLines(text);
  const tooMany = !isTable && items.length > MAX_IDEAS;
  const single = !isTable && !text.trim().includes('\n');
  const ready = repository !== '' && items.length > 0 && !tooMany && !busy;

  const submit = (): void => {
    if (!ready) return;
    setError(null);
    setBusy(true);
    createIdeaBatch({
      title: quickAddTitle(items),
      repositories: [{ repositoryId: repository, role: 'primary' }],
      ...(isTable ? { table: text } : { seeds: items }),
      campaignId: campaignId === '' ? null : campaignId,
      language: null,
      address: null,
    })
      .then(() => {
        writeLastRepository(repository);
        setText('');
        onAdded(items.length);
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Enter' || event.target !== textRef.current || event.shiftKey || event.altKey) return;
    if (event.ctrlKey || event.metaKey || single) {
      event.preventDefault();
      submit();
    }
  };

  if (repositories !== null && usable.length === 0) {
    return (
      <div className="board-quick-add" onKeyDown={onKeyDown} role="group" aria-label={wording.QUICK_ADD}>
        <Notice kind="info">
          {wording.QUICK_ADD_NEEDS_REPOSITORY}{' '}
          <Link className="link" href="/repositories">
            {wording.QUICK_ADD_TO_REPOSITORIES}
          </Link>
        </Notice>
        <div className="board-quick-add__actions">
          <button type="button" className="button button--small button--quiet" onClick={onClose}>
            {wording.CLOSE}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="board-quick-add"
      aria-label={wording.QUICK_ADD}
      onKeyDown={onKeyDown}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="visually-hidden" htmlFor={`${id}-ideas`}>
        {wording.ONE_IDEA_PER_LINE}
      </label>
      <textarea
        id={`${id}-ideas`}
        ref={textRef}
        className="field__input field__textarea board-quick-add__text"
        rows={3}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
        placeholder={wording.QUICK_ADD_PLACEHOLDER}
        aria-describedby={`${id}-hint`}
        aria-invalid={tooMany}
        disabled={busy}
      />
      <select
        className="field__input field__input--inline"
        value={repository}
        onChange={(event) => {
          setRepositoryId(event.target.value);
        }}
        aria-label={wording.QUICK_ADD_REPOSITORY}
        disabled={repositories === null}
      >
        {usable.map((one) => (
          <option key={one.id} value={one.id}>
            {one.name}
          </option>
        ))}
      </select>
      <select
        className="field__input field__input--inline"
        value={campaignId}
        onChange={(event) => {
          setCampaignId(event.target.value);
        }}
        aria-label={wording.QUICK_ADD_CAMPAIGN}
      >
        <option value="">{wording.BOARD_NO_CAMPAIGN}</option>
        {campaigns.map((campaign) => (
          <option key={campaign.id} value={campaign.id}>
            {campaign.name}
          </option>
        ))}
      </select>
      <p className={`field__hint${tooMany ? ' field__hint--error' : ''}`} id={`${id}-hint`} aria-live="polite">
        {tooMany
          ? wording.tooManyIdeas(items.length, MAX_IDEAS)
          : isTable
            ? wording.tableRows(items.length)
            : wording.QUICK_ADD_HINT}
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <div className="board-quick-add__actions">
        <button type="submit" className="button button--small button--primary" disabled={!ready}>
          {busy ? wording.QUICK_ADD_ADDING : wording.QUICK_ADD_SUBMIT}
        </button>
        <button type="button" className="button button--small button--quiet" onClick={onClose}>
          {wording.CLOSE}
        </button>
      </div>
    </form>
  );
}
